import { Link } from 'react-router-dom';

function Privacy() {
  return (
    <div className="min-h-screen bg-puzzle-surface">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <h1 className="text-3xl font-bold text-puzzle-text mb-2">Privacy Policy</h1>
        <p className="text-puzzle-text-secondary mb-8">Effective Date: January 2026</p>

        {/* Table of Contents */}
        <nav className="mb-10 p-4 bg-puzzle-background rounded-lg">
          <h2 className="font-semibold text-puzzle-text mb-3">Contents</h2>
          <ol className="list-decimal list-inside space-y-1 text-puzzle-blue text-sm">
            <li><a href="#intro" className="hover:underline">Introduction</a></li>
            <li><a href="#collect" className="hover:underline">Information We Collect</a></li>
            <li><a href="#use" className="hover:underline">How We Use Your Information</a></li>
            <li><a href="#storage" className="hover:underline">Data Storage</a></li>
            <li><a href="#retention" className="hover:underline">Data Retention</a></li>
            <li><a href="#sharing" className="hover:underline">Data Sharing</a></li>
            <li><a href="#rights" className="hover:underline">Your Rights</a></li>
            <li><a href="#security" className="hover:underline">Security</a></li>
            <li><a href="#children" className="hover:underline">Children&apos;s Privacy</a></li>
            <li><a href="#changes" className="hover:underline">Changes to This Policy</a></li>
          </ol>
        </nav>

        {/* Content */}
        <div className="space-y-8 text-puzzle-text">
          <section id="intro" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p>
              At Puzzlists, we respect your privacy and are committed to protecting your
              personal information. This Privacy Policy explains what data we collect,
              how we use it, and your rights regarding your information.
            </p>
          </section>

          <section id="collect" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>

            <h3 className="font-medium mt-4 mb-2">Account Information</h3>
            <p className="text-puzzle-text-secondary mb-2">
              If you create an account, we collect:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Email address</li>
              <li>Username</li>
              <li>Password (stored securely using industry-standard hashing)</li>
            </ul>

            <h3 className="font-medium mt-4 mb-2">Profile Information (Optional)</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>First and last name</li>
              <li>Display name</li>
              <li>Bio</li>
              <li>Profile picture</li>
            </ul>

            <h3 className="font-medium mt-4 mb-2">Gameplay Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Puzzle progress and piece positions</li>
              <li>Completion times and move counts</li>
              <li>Device type (mobile, tablet, or desktop) for display optimization</li>
            </ul>

            <h3 className="font-medium mt-4 mb-2">Guest Users</h3>
            <p className="text-puzzle-text-secondary">
              If you play without an account, we generate an anonymous identifier stored
              in your browser to save your puzzle progress. This identifier cannot be
              used to identify you personally.
            </p>
          </section>

          <section id="use" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">
              We use your information to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Provide the puzzle game service</li>
              <li>Save and sync your puzzle progress across devices</li>
              <li>Display your completion statistics</li>
              <li>Authenticate your account</li>
              <li>Improve and optimize the service</li>
            </ul>
          </section>

          <section id="storage" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">4. Data Storage</h2>
            <p className="mb-3">
              Your data is stored in the following ways:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-puzzle-text-secondary">
              <li>
                <strong>Database:</strong>
                {' '}
                Account information, puzzle progress, and statistics are stored securely
              </li>
              <li>
                <strong>Browser Storage:</strong>
                {' '}
                Authentication tokens and preferences are stored locally in your browser
              </li>
            </ul>
            <p className="mt-3 font-medium">
              We do not use cookies. All local data is stored using browser local storage.
            </p>
          </section>

          <section id="retention" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
            <ul className="list-disc list-inside space-y-2 ml-4 text-puzzle-text-secondary">
              <li>
                <strong>Account Data:</strong>
                {' '}
                Retained until you delete your account
              </li>
              <li>
                <strong>Guest Data:</strong>
                {' '}
                Automatically deleted after 7 days of inactivity
              </li>
              <li>
                <strong>Puzzle Progress:</strong>
                {' '}
                Retained with your account or for 7 days for guest users
              </li>
            </ul>
          </section>

          <section id="sharing" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">6. Data Sharing</h2>
            <p className="mb-3 font-medium">
              We do not sell your personal information.
            </p>
            <p className="mb-3">
              We do not use third-party advertising or tracking services.
            </p>
            <p className="mb-3">
              We may share data only in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>With service providers who help us operate the service (e.g., hosting)</li>
              <li>When required by law or to protect our legal rights</li>
              <li>In connection with a merger or acquisition (with notice to users)</li>
            </ul>
          </section>

          <section id="rights" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
            <p className="mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-puzzle-text-secondary">
              <li>
                <strong>Access:</strong>
                {' '}
                View your profile and puzzle data through your
                {' '}
                <Link to="/account" className="text-puzzle-blue hover:underline">Account Settings</Link>
              </li>
              <li>
                <strong>Update:</strong>
                {' '}
                Modify your profile information at any time
              </li>
              <li>
                <strong>Delete:</strong>
                {' '}
                Permanently delete your account and all associated data
              </li>
            </ul>
            <p className="mt-3">
              To exercise these rights, visit your
              {' '}
              <Link to="/account" className="text-puzzle-blue hover:underline">Account Settings</Link>
              {' '}
              page.
            </p>
          </section>

          <section id="security" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">8. Security</h2>
            <p className="mb-3">
              We implement appropriate security measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Passwords are securely hashed before storage</li>
              <li>Authentication uses secure tokens</li>
              <li>All connections are encrypted using HTTPS</li>
            </ul>
            <p className="mt-3">
              While we take reasonable precautions, no internet transmission is completely
              secure. Please use a strong, unique password for your account.
            </p>
          </section>

          <section id="children" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">9. Children&apos;s Privacy</h2>
            <p>
              Puzzlists is not intended for children under 13 years of age. We do not
              knowingly collect personal information from children under 13. If we
              discover that we have collected data from a child under 13, we will
              delete it promptly.
            </p>
          </section>

          <section id="changes" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make
              significant changes, we will update the effective date at the top of
              this page. We encourage you to review this policy periodically.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-puzzle-border">
          <p className="text-puzzle-text-secondary text-sm">
            See also our
            {' '}
            <Link to="/terms" className="text-puzzle-blue hover:underline">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
